import { DateTime } from '@ksyos/date-time';
import { Button } from '@ksyos/design-system';
import { every, isNull, values } from 'lodash';
import { klachtenEcgCustomControlDataSchema } from '@common/validation/customControls/cardiology/holterConsult/cardiology';
import type { ValidationErrors } from '../../../../../utils/validation';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../../utils/validation';
import { DateInput, InputTriState, NullableInputText } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { Modal } from '../../../../elements/modals/Modal';
import { AnamneseType } from './AnamneseType';
import { AnamneseWrapper } from './AnamneseWrapper';

interface IKlachtenAddEditModalProps {
    anamneseData: IKlachten | null;
    deleteAnamneseData: () => Promise<void>;
    onClose: () => Promise<void>;
    setAnamneseData: (item?: IKlachten) => Promise<void>;
}

interface IKlachtenAddEditModalState {
    anamneseData: IKlachten;
    errors: ValidationErrors;
}

export interface IKlachten {
    collaps: boolean | null;
    conditievermindering: boolean | null;
    dyspnoe: boolean | null;
    hartgeruis: boolean | null;
    klachten_sinds: string | null;
    klachten_tijdens_het_maken_van_het_ECG: boolean | null;
    klachtenvrij: boolean | null;
    moeheid: boolean | null;
    overig: string | null;
    palpitaties: boolean | null;
    pijn_op_de_borst: boolean | null;
    screening: boolean | null;
}

export class KlachtenAddEditModal extends Component<IKlachtenAddEditModalProps, IKlachtenAddEditModalState> {
    constructor(props: IKlachtenAddEditModalProps) {
        super(props);
        this.state = {
            anamneseData: {
                klachten_sinds: null,
                klachten_tijdens_het_maken_van_het_ECG: null,
                pijn_op_de_borst: null,
                dyspnoe: null,
                palpitaties: null,
                moeheid: null,
                conditievermindering: null,
                hartgeruis: null,
                collaps: null,
                klachtenvrij: null,
                screening: null,
                overig: null,

                ...this.props.anamneseData,
            },
            errors: emptyValidationErrors,
        };
    }

    public render() {
        return (
            <Modal
                actionButtons={[
                    <Button
                        onClick={async () => await this.props.deleteAnamneseData()}
                        variant="outline"
                    >
                        Verwijder
                    </Button>,
                    <Button
                        className="automation-cardioButtonSaveAnamnesis"
                        onClick={async () => await this.validateAnamneseData()}
                        variant="solid"
                    >
                        Anamnese opslaan
                    </Button>,
                ]}
                isOpen
                onClose={async () => await this.props.onClose()}
                size="medium"
                title="Klachten"
            >
                <AnamneseWrapper anamneseType={AnamneseType.klachten}>
                    <HorizontalFormGroup hasErrors={this.state.errors.has('klachten_sinds')}>
                        <FormLabel>Klacht sinds</FormLabel>
                        <InputContainer size="large">
                            <DateInput
                                error={this.state.errors.get('klachten_sinds')}
                                onChange={async (value) => await this.handleChange('klachten_sinds', value)}
                                onDoValidate={async (date) => await this.validateDate(date)}
                                placeholder="Klacht sinds"
                                value={this.state.anamneseData.klachten_sinds}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                    <HorizontalFormGroup hasErrors={this.state.errors.has('klachten_tijdens_het_maken_van_het_ECG')}>
                        <FormLabel>Klachten tijdens het maken van het ECG?</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('klachten_tijdens_het_maken_van_het_ECG')}
                                name="klachten_tijdens_het_maken_van_het_ECG"
                                onChange={async (value) =>
                                    await this.handleChange('klachten_tijdens_het_maken_van_het_ECG', value)
                                }
                                onDoValidate={async () =>
                                    await this.validateOne('klachten_tijdens_het_maken_van_het_ECG')
                                }
                                value={this.state.anamneseData.klachten_tijdens_het_maken_van_het_ECG}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('pijn_op_de_borst')}>
                        <FormLabel>Pijn op de borst</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('pijn_op_de_borst')}
                                name="pijn_op_de_borst"
                                onChange={async (value) => await this.handleChange('pijn_op_de_borst', value)}
                                onDoValidate={async () => await this.validateOne('pijn_op_de_borst')}
                                value={this.state.anamneseData.pijn_op_de_borst}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('dyspnoe')}>
                        <FormLabel>Dyspnoe</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('dyspnoe')}
                                name="dyspnoe"
                                onChange={async (value) => await this.handleChange('dyspnoe', value)}
                                onDoValidate={async () => await this.validateOne('dyspnoe')}
                                value={this.state.anamneseData.dyspnoe}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('palpitaties')}>
                        <FormLabel>Palpitaties</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('palpitaties')}
                                name="palpitaties"
                                onChange={async (value) => await this.handleChange('palpitaties', value)}
                                onDoValidate={async () => await this.validateOne('palpitaties')}
                                value={this.state.anamneseData.palpitaties}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('moeheid')}>
                        <FormLabel>Moeheid</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('moeheid')}
                                name="moeheid"
                                onChange={async (value) => await this.handleChange('moeheid', value)}
                                onDoValidate={async () => await this.validateOne('moeheid')}
                                value={this.state.anamneseData.moeheid}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('conditievermindering')}>
                        <FormLabel>Conditievermindering</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('conditievermindering')}
                                name="conditievermindering"
                                onChange={async (value) => await this.handleChange('conditievermindering', value)}
                                onDoValidate={async () => await this.validateOne('conditievermindering')}
                                value={this.state.anamneseData.conditievermindering}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('hartgeruis')}>
                        <FormLabel>Hartgeruis</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('hartgeruis')}
                                name="hartgeruis"
                                onChange={async (value) => await this.handleChange('hartgeruis', value)}
                                onDoValidate={async () => await this.validateOne('hartgeruis')}
                                value={this.state.anamneseData.hartgeruis}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('collaps')}>
                        <FormLabel>Collaps</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('collaps')}
                                name="collaps"
                                onChange={async (value) => await this.handleChange('collaps', value)}
                                onDoValidate={async () => await this.validateOne('collaps')}
                                value={this.state.anamneseData.collaps}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('klachtenvrij')}>
                        <FormLabel>Klachtenvrij</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('klachtenvrij')}
                                name="klachtenvrij"
                                onChange={async (value) => await this.handleChange('klachtenvrij', value)}
                                onDoValidate={async () => await this.validateOne('klachtenvrij')}
                                value={this.state.anamneseData.klachtenvrij}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('screening')}>
                        <FormLabel>Screening</FormLabel>
                        <InputContainer>
                            <InputTriState
                                error={this.state.errors.get('screening')}
                                name="screening"
                                onChange={async (value) => await this.handleChange('screening', value)}
                                onDoValidate={async () => await this.validateOne('screening')}
                                value={this.state.anamneseData.screening}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup hasErrors={this.state.errors.has('overig')}>
                        <FormLabel>Overig</FormLabel>
                        <InputContainer>
                            <NullableInputText
                                error={this.state.errors.get('overig')}
                                onChange={async (value) => await this.handleChange('overig', value)}
                                onDoValidate={async () => await this.validateOne('overig')}
                                value={this.state.anamneseData.overig}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                </AnamneseWrapper>
            </Modal>
        );
    }

    private async handleChange<K extends keyof IKlachten>(name: K, value: IKlachten[K]) {
        await this.setState(({ anamneseData }) => ({ anamneseData: { ...anamneseData, [name]: value } }));
    }

    private async validateOne(name: keyof IKlachten) {
        await this.setState(({ anamneseData, errors }) => ({
            errors: updateValidationErrorsForOne(klachtenEcgCustomControlDataSchema, anamneseData, errors, name),
        }));
    }

    private async validateAnamneseData() {
        if (every(values(this.state.anamneseData), isNull)) {
            return;
        }

        if (DateTime.from(this.state.anamneseData.klachten_sinds!).isAfter(DateTime.now(), 'day')) {
            return;
        }

        const { hasErrors, errors, item } = validate(klachtenEcgCustomControlDataSchema, this.state.anamneseData);
        await this.setState({ errors });
        if (!hasErrors) {
            await this.props.setAnamneseData(item);
        }
    }

    private async validateDate(value: string | null) {
        if (!value || !DateTime.from(value).isValid()) {
            await this.setState(({ errors }) => ({ errors: errors.set('klachten_sinds', 'any_required') }));

            return false;
        } else if (DateTime.from(value).isAfter(DateTime.now(), 'day')) {
            await this.setState(({ errors }) => ({ errors: errors.set('klachten_sinds', 'date_future') }));

            return false;
        } else {
            await this.setState(({ errors }) => ({ errors: errors.remove('klachten_sinds') }));
        }

        return true;
    }
}
